import { FormArray, FormGroup } from '@angular/forms';
import { RelationshipInformation } from '../../../../interfaces/referralSections.interface';

// Required person fields per parent/guardian row (all name parts + DOB + relationship).
const PERSON_REQUIRED = ['fname', 'lname', 'dob', 'relationToParticipant'];
// Required address fields (ppl-address control names) per row.
const ADDRESS_REQUIRED = ['street1', 'city', 'state', 'zipCode', 'countyOrRegion'];
function trimmed(control: any): any {
  const value = control?.value;
  return typeof value === 'string' ? value.trim() : value;
}

// Matches DateTimeFormatterService.formatDateToString (MM/dd/yyyy) so the util stays dependency-free.
function dateToString(date: Date): string {
  const day = ('0' + date.getDate()).slice(-2);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  return `${month}/${day}/${date.getFullYear()}`;
}

/**
 * Validate the parent/guardian rows. Returns namespaced person-field errors
 * (`relationshipInformation_{i}_{field}` — used to block Next/Save) and per-row address error bags
 * (index → { street1, ... } — handed to each ppl-address). Skips only the NA second parent: every
 * row that is on screen must be complete, and an unwanted 3rd/4th row is removed with its delete
 * icon rather than left blank. Caller decides whether the section is shown before calling.
 */
export function validateParentGuardians(parents: FormArray): {
  fieldErrors: Record<string, string>;
  addressErrors: Record<number, Record<string, string>>;
} {
  const fieldErrors: Record<string, string> = {};
  const addressErrors: Record<number, Record<string, string>> = {};

  parents.controls.forEach((ctrl, i) => {
    const group = ctrl as FormGroup;
    if (group.get('notApplicable')?.value) return; // second parent marked Not Applicable

    PERSON_REQUIRED.forEach((field) => {
      if (!trimmed(group.get(field))) {
        fieldErrors[`relationshipInformation_${i}_${field}`] = 'This field is required';
      }
    });

    const address = group.get('address') as FormGroup;
    const bag: Record<string, string> = {};
    ADDRESS_REQUIRED.forEach((field) => {
      if (!trimmed(address?.get(field))) {
        bag[field] = 'This field is required';
        fieldErrors[`relationshipInformation_${i}_address_${field}`] = 'This field is required';
      }
    });
    if (Object.keys(bag).length) addressErrors[i] = bag;
  });

  return { fieldErrors, addressErrors };
}

/**
 * Map form rows (from parents.getRawValue()) to the saveParticipantEnrollmentInfo payload shape:
 * drop the NA second parent + empty optional rows, strip the frontend-only `notApplicable` flag and
 * the ppl-address-only `psd_code`/`mailing`, default `id` to 0 (new), format dob to MM/dd/yyyy, and
 * spread the address fields flat (street1/street2/city/state/zipCode/countyOrRegion).
 */
export function mapParentGuardiansForSave(rows: any[]): RelationshipInformation[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((rec) => {
      const { notApplicable, dob, id, address, fname, mname, lname, relationToParticipant } = rec || {};
      // "Not Applicable" second parent: clear an existing record (id > 0) by sending empty details so
      // the backend removes previously saved info; drop a brand-new one (nothing to clear).
      if (notApplicable) {
        return id
          ? { id, fname: '', mname: '', lname: '', relationToParticipant: '', dob: '', street1: '', street2: '', city: '', state: '', zipCode: '', countyOrRegion: '' }
          : null;
      }
      const { psd_code, mailing, ...addr } = address || {};
      return {
        id: id ?? 0,
        fname: fname,
        mname: mname,
        lname: lname,
        relationToParticipant: relationToParticipant,
        dob: dob instanceof Date ? dateToString(dob) : (dob ?? ''),
        ...addr, // street1, street2, city, state, zipCode, countyOrRegion
      };
    })
    .filter((rec): rec is any => !!rec)
    // Keep cleared existing records (id > 0) even though empty; drop empty brand-new rows.
    .filter((rec) => (rec.id && rec.id > 0) || hasParentGuardianData(rec));
}

function hasParentGuardianData(rec: any): boolean {
  return ['fname', 'mname', 'lname', 'dob', 'relationToParticipant', 'street1', 'street2', 'city', 'state', 'zipCode', 'countyOrRegion']
    .some((f) => {
      const v = rec[f];
      return typeof v === 'string' ? v.trim() !== '' : v != null;
    });
}

/** True when a saved parent record carries real data (used to decide NA-by-default on load). */
export function parentRecordHasData(rec: any): boolean {
  return hasParentGuardianData(rec);
}

/**
 * Doc-type codes required for the parents entered: N real parents → ['PAR1'..'PAR{N}'].
 * Uses the same NA/empty stripping as save, so the count matches saved records.
 *
 * Nothing is required unless the participant is a minor. An adult can still carry relationship
 * records (entered earlier, or kept after they turned 18), but upload-docs hides every PAR doc type
 * for adults — so requiring them would block the step on documents that cannot be selected.
 */
export function requiredParentDocCodes(relationshipInformation: any[], isMinor = true): string[] {
  if (!isMinor) return [];
  // Count only real parents (records with data); cleared "Not Applicable" records are excluded.
  const n = mapParentGuardiansForSave(relationshipInformation || []).filter((rec) => hasParentGuardianData(rec)).length;
  return Array.from({ length: n }, (_, i) => `PAR${i + 1}`);
}

/** Required PAR codes not present among the uploaded document-type codes. */
export function missingParentDocCodes(
  relationshipInformation: any[],
  uploadedDocCodes: string[],
  isMinor = true
): string[] {
  const uploaded = new Set(uploadedDocCodes || []);
  return requiredParentDocCodes(relationshipInformation, isMinor).filter((code) => !uploaded.has(code));
}
